/**
 * 与业务无关的公共方法
 * @module util-function
 */
import { MessageBox } from 'element-ui'
import { downloadFileUrl } from 'comm/src/config'

/**
 * @description 地图信息窗体定位
 * @param {Array} pos
 * @param {Number} size
 * @return {String} positioning 地图定位信息
 */
export function getPositioning(pos, size) {
  const offsetX = 20 // 弹窗在x方向上与鼠标的间距
  const offsetY = 20 // 弹窗在y方向上与鼠标的间距
  const pointX = pos[0] // 鼠标x轴的位置
  const pointY = pos[1] // 鼠标y轴的位置
  const contentHeight = size.contentSize[1] + offsetY // 内容高度
  const halfWidth = size.contentSize[0] / 2 + offsetX // 内容宽度
  const halfHeight = size.contentSize[1] / 2 + offsetY // 内容高度
  const viewWidth = size.viewSize[0] // 图表宽度
  const viewHeight = size.viewSize[1] // 图表高度
  const reverseX = viewWidth - pointX // 鼠标x轴的位置
  const reverseY = viewHeight - pointY // 鼠标y轴的位置
  let positioning = ['center']
  // 在容器范围内，基于鼠标点击位置，右边、左边、上边、下边，划分四个矩形
  if (pointY > contentHeight) {
    if (pointX > halfWidth) {
      if (reverseX > halfWidth) {
        positioning.unshift('bottom')
      } else {
        if (reverseY > halfHeight) {
          positioning.push('right')
        } else {
          positioning = ['bottom', 'right']
        }
      }
    } else {
      if (reverseY > halfHeight) {
        positioning.push('left')
      } else {
        positioning = ['bottom', 'left']
      }
    }
  } else {
    if (pointX > halfWidth) {
      if (reverseX > halfWidth) {
        positioning.unshift('top')
      } else {
        if (pointY > halfHeight) {
          positioning.push('right')
        } else {
          positioning = ['top', 'right']
        }
      }
    } else {
      if (pointY > halfHeight) {
        positioning.push('left')
      } else {
        positioning = ['top', 'left']
      }
    }
  }
  return positioning.join('-')
}

/**
 * @description 为数值添加逗号
 * @param {Number} val
 * @param {Number} num
 */
export function splitNumber(value, num = 3) {
  if (value === null || undefined) return value
  if (isNaN(Number(value))) return value
  const val =
    value.toString().indexOf('.') === -1 ? value : Number(value).toFixed(2)
  const [str, arr] = [val.toString(), []]
  let flag = ''
  // if (str.indexOf('.') !== -1) {
  //   strs = str.split('.')[0]
  // } else {
  //   strs = str.split('')
  // }
  const strs = str.split('.')
  if (strs[0].includes('-')) {
    flag = '-'
    strs[0] = strs[0].substr(1)
  }
  if (strs.length === 2) {
    strs[0]
      .split('')
      .reverse()
      .forEach((item, index) => {
        arr.push(item)
        if (
          (index + 1) % num === 0 &&
          index !== strs[0].length - 1 &&
          index !== 0
        ) {
          arr.push(',')
        }
      })
    return flag + arr.reverse().join('') + '.' + strs[1].slice(0, 2)
  } else {
    strs[0]
      .split('')
      .reverse()
      .forEach((item, index) => {
        arr.push(item)
        if (
          (index + 1) % num === 0 &&
          index !== strs[0].length - 1 &&
          index !== 0
        ) {
          arr.push(',')
        }
      })
    return flag + arr.reverse().join('')
  }
  // const strs = str.split('')
  // strs.reverse().forEach((item, index) => {
  //   arr.push(item)
  //   if (((index + 1) % num === 0) && (index !== strs.length - 1) && index !== 0) {
  //     arr.push(',')
  //   }
  // })
  // return arr.reverse().join('')
}

/**
 * 根据属性值过滤对象，并返回一个新的对象(去掉对象中值为空的属性)
 * @function filterObject
 * @param {obj} obj - 被过滤的对象
 * @param {Array} keys - 过滤所依据的key值
 * @returns {object}
 * @example
 * import { filterObject } from 'comm/src/normal/fun'
 * let obj = { name: 12, age: 3 }
 * let keys = ['name']
 * filterObject(obj, keys)
 **/
export const filterObject = (obj, keys) => {
  const [result, _obj] = [{}, Object.assign({}, obj)]
  for (const key in _obj) {
    if (_obj[key] !== 0 && !_obj[key]) {
      // 属性值为空
      continue
    }
    if (keys.includes(key)) {
      result[key] = _obj[key]
    }
  }
  return result
}

/**
 * 生成全局唯一标识
 */
export const uuid = () => {
  let d = Date.now()
  if (
    typeof performance !== 'undefined' &&
    typeof window.performance.now === 'function'
  ) {
    d += window.performance.now() // use high-precision timer if available
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (d + Math.random() * 16) % 16 | 0
    d = Math.floor(d / 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/**
 * 去掉对象中值为空的属性，并返回一个新的对象
 * @function delNullProperty
 * @returns {object}
 * @example
 * import { delNullProperty } from 'comm/src/normal/fun'
 * delNullProperty()
 **/
export const delNullProperty = (obj) => {
  const _obj = Object.assign({}, obj)
  for (const key in _obj) {
    if (
      _obj[key] === '' ||
      _obj[key] === null ||
      JSON.stringify(_obj[key]) === '[]'
    ) {
      delete _obj[key]
    }
  }
  return _obj
}
/**
 * 把对象中值为null的属性的值改为空字符，并返回一个新的对象
 * @function changeNullPropertyToNullString
 * @returns {object}
 * @example
 * import { changeNullPropertyToNullString } from 'comm/src/normal/fun'
 * changeNullPropertyToNullString()
 **/
export const changeNullPropertyToNullString = (obj) => {
  const _obj = Object.assign({}, obj)
  for (const key in _obj) {
    if (_obj[key] === null) {
      _obj[key] = ''
    }
  }
  return _obj
}
/**
 * 询问用户是否保存修改
 * @function isSaveChange
 * @returns null
 * @param {Function} changeFlag - 判断变化的值
 * @param {Function} sureCallback - 用户点击确定后的回调
 * @param {Function} cancelCallback - 用户点击取消后的回调
 * @example
 * import {isSaveChange} from 'comm/src/normal/fun'
 * isSaveChange()
 **/
export const isSaveChange = (changeFlag, sureCallback, cancelCallback) => {
  if (changeFlag) {
    MessageBox.confirm('您未保存修改！您确定离开？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })
      .then(() => {
        if (sureCallback) sureCallback()
      })
      .catch(() => {
        if (cancelCallback) cancelCallback()
      })
  } else {
    if (sureCallback) sureCallback()
  }
}
/**
 * 询问用户是否离开页面
 * @function isLeaveCurrentPage
 * @returns null
 * @param {Function} changeFlag - 判断变化的值
 * @param {Function} next - vue-router 导航守卫函数中的next参数对象
 * @param {Function} sureCallback - 用户点击确定后的回调
 * @param {Function} cancelCallback - 用户点击取消后的回调
 * @example
 * import {isLeaveCurrentPage} from 'comm/src/normal/fun'
 * isLeaveCurrentPage()
 **/
export const isLeaveCurrentPage = (
  changeFlag,
  next,
  sureCallback,
  cancelCallback
) => {
  if (changeFlag) {
    MessageBox.confirm('您未保存修改！您确定离开本页面？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })
      .then(() => {
        if (sureCallback) sureCallback()
        next()
      })
      .catch(() => {
        next(false)
        if (cancelCallback) cancelCallback()
      })
  } else {
    next()
  }
}

/**
 * @desc 排序方法 该方法使用于对对象数组排序，会改变原有数组
 * @function sortObject
 * @param {Object} params - 参数对象
 * @param {Object[]} params.data - 需要排序的对象数组
 * @param {String} params.sortKey - 排序依据的属性
 * @param {Function} [params.formatFun = (val) => {return val}] - 对属性值进行格式化的函数
 * @param {Boolean} [params.order = true] - 排列顺序 false 为倒序 true为正序
 * @returns {Object[]} - 排序后的数组
 * @example
 * import {sortObject} from 'comm/src/normal/fun'
 * sortObject({
 *  data: [{age: 7}, {age: 5}],
 *  sortKey: 'age'
 * }) //  [{age: 5}, {age: 7}]
 */
export const sortObject = ({ data, sortKey, formatFun, order }) => {
  if (!data) throw new Error('待排序的数据为空')
  if (!sortKey) throw new Error('排序字段为空')
  if (!formatFun) {
    formatFun = (val) => {
      return val
    }
  }
  if (order === undefined) order = true // 默认正序
  let [arr, first, second] = [data, 0, 0]
  console.log(order)
  arr.sort((x, y) => {
    first = formatFun(x[sortKey])
    second = formatFun(y[sortKey])
    if (first < second) return order ? -1 : 1
    if (first === second) return 0
    if (first > second) return order ? 1 : -1
  })
  return arr
}
/**
 * @desc 去重方法 该方法使用于对对象数组去重，会改变原有数组
 * @function sortObject
 * @param {Object} params - 参数对象
 * @param {Object[]} params.data - 需要去重的对象数组
 * @param {String} params.sortKey - 去重依据的属性
 * @returns {Object[]} - 去重后的数组
 * @example
 * import {sortObject} from 'comm/src/normal/fun'
 * sortObject({
 *  data: [{age: 7}, {age: 5}, {age: 5}],
 *  sortKey: 'age'
 * }) //  [{age: 5}, {age: 7}]
 */
export const repeatObject = ({ data, sortKey }) => {
  if (!data) throw new Error('待去重的数据为空')
  if (!sortKey) throw new Error('去重字段为空')
  let [arr, hash] = [data, {}]
  arr = arr.reduce((newArr, curVal) => {
    if (!hash[curVal[sortKey]]) hash[curVal[sortKey]] = newArr.push(curVal)
    return newArr
  }, [])
  return arr
}

/**
 * 从url字符串中获取参数
 * @param name  参数的key
 * @returns {*}  参数值
 */
export const getQueryString = function (name) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
  const r = window.location.search.substr(1).match(reg)
  if (r != null) return unescape(r[2])
  return ''
}
// 成功改变流程的状态后 通知父框架(iframe外部)
export const getBackList = function (event) {
  window.parent.postMessage(event || 'reflesh', '*')
}

/**
 *从数组中找到重复的元素
 *返回重复字段的数组
 * @param {*} arr 需要查出重复字段的数组
 * @returns 重复字段的数组
 */
export const getSame = function (arr) {
  var res = []
  arr.sort()
  for (var i = 0; i < arr.length; i++) {
    if (
      arr.indexOf(arr[i]) !== arr.lastIndexOf(arr[i]) &&
      res.indexOf(arr[i]) === -1
    ) {
      res.push(arr[i])
    }
    i = arr.lastIndexOf(arr[i])
  }
  return res
}
/**
 * 将数组中某一个有重复的键值排列，不重复的排前面，重复的排后面，并用数字添加标明是第几个
 * [{'name':t},{'name':k},{'name':l},{'name':s},{'name':l},{'name':l},{'name':k}]=>[{'name':s},{'name':t},{'name':k1},{'name':k2},{'name':l1},{'name':l2},{'name':l3}]
 * @param {*} arr [{'name':t},{'name':k},{'name':l},{'name':s},{'name':l},{'name':l},{'name':k}]
 * @param {*} key name
 * @returns
 */
export const rearrangementLenged = function (arr, key) {
  const arrList = []
  let sameList = []
  const newList = []
  arr.map((val) => {
    arrList.push(val[key])
  })
  sameList = getSame(arrList)
  arr.map((val) => {
    if (!sameList.includes(val[key])) {
      newList.push(val)
    }
  })
  let num = 1
  for (let i = 0; i < sameList.length; i++) {
    const ele = sameList[i]
    for (let j = 0; j < arr.length; j++) {
      const ELE = arr[j]
      if (ele === ELE.name) {
        ELE.name = ELE.name + num
        newList.push(ELE)
        num = num + 1
      }
    }
    num = 1
  }
  return newList
}

// 获取relation的label值
export function getLabel(options, value) {
  const obj = options.find((val) => {
    return val.value === value
  })
  return obj ? obj.label : ''
}

// 前端分页
export function byPage(data, pageSize, pageIndex) {
  const startIndex = (pageIndex - 1) * pageSize
  const endIndex = startIndex + pageSize
  return data.slice(startIndex, endIndex)
}

/**
 * 根据表格某一列的值定位到某一行
 * @param value 值
 * @param key 列名所对应的key
 * @param tableObj element-ui表格组件对象
 */
export function locationLine(value, key, tableObj) {
  if (!(value && key)) return false // 表格数据为空
  let [ind, tableObj$el, tableData] = [null, tableObj.$el, tableObj.data]
  tableData.forEach((val, index) => {
    if (val && (val[key] + '').toString() === value.toString()) {
      ind = index
    }
  })
  if (ind === null) return
  tableObj.setCurrentRow(tableData[ind])
  const targetTop = tableObj$el
    .querySelectorAll('.el-table__body tr')
    [ind].getBoundingClientRect().top
  const containerTop = tableObj$el
    .querySelector('.el-table__body')
    .getBoundingClientRect().top
  const scrollParent = tableObj$el.querySelector('.el-table__body-wrapper')
  scrollParent.scrollTop = targetTop - containerTop
}

/**
 * 格式化小数
 * @param {*} val 小数值
 * @param {*} num 要保留的数位
 */
export function formatDecimal(val, num = 2) {
  if (val === undefined || val === null) {
    return '-'
  }
  return Number(val).toFixed(num)
}

// 通过a标签下载文件
export function downloadFile(id) {
  let a = document.getElementById('downloadFile')
  if (!a) {
    a = document.createElement('a')
    a.setAttribute('id', 'downloadFile')
  }
  a.href = downloadFileUrl + '/' + id
  a.click()
}

// 节流 todo 实际没有生效 得重新改这个方法  从它和lodash里的方法选一个，统一
export function debounce(func, wait, immediate) {
  let timeout, args, context, timestamp, result
  const later = function () {
    // 据上一次触发时间间隔
    const last = +new Date() - timestamp
    // 上次被包装函数被调用时间间隔last小于设定时间间隔wait
    if (last < wait && last > 0) {
      timeout = setTimeout(later, wait - last)
    } else {
      timeout = null
      // 如果设定为immediate===true，因为开始边界已经调用过了此处无需调用
      if (!immediate) {
        result = func.apply(context, args)
        if (!timeout) context = args = null
      }
    }
  }
  return function (...args) {
    context = this
    timestamp = +new Date()
    const callNow = immediate && !timeout
    // 如果延时不存在，重新设定延时
    if (!timeout) timeout = setTimeout(later, wait)
    if (callNow) {
      result = func.apply(context, args)
      context = args = null
    }
    return result
  }
}

/**
 * 生成雪花数
 */

export let Snowflake = (function () {
  function Snowflake(_workerId, _dataCenterId, _sequence) {
    this.twepoch = 1288834974657n
    //this.twepoch = 0n;
    this.workerIdBits = 5n
    this.dataCenterIdBits = 5n
    this.maxWrokerId = -1n ^ (-1n << this.workerIdBits) // 值为：31
    this.maxDataCenterId = -1n ^ (-1n << this.dataCenterIdBits) // 值为：31
    this.sequenceBits = 12n
    this.workerIdShift = this.sequenceBits // 值为：12
    this.dataCenterIdShift = this.sequenceBits + this.workerIdBits // 值为：17
    this.timestampLeftShift =
      this.sequenceBits + this.workerIdBits + this.dataCenterIdBits // 值为：22
    this.sequenceMask = -1n ^ (-1n << this.sequenceBits) // 值为：4095
    this.lastTimestamp = -1n
    //设置默认值,从环境变量取
    this.workerId = 1n
    this.dataCenterId = 1n
    this.sequence = 0n
    if (this.workerId > this.maxWrokerId || this.workerId < 0) {
      throw new Error(
        '_workerId must max than 0 and small than maxWrokerId-[' +
          this.maxWrokerId +
          ']'
      )
    }
    if (this.dataCenterId > this.maxDataCenterId || this.dataCenterId < 0) {
      throw new Error(
        '_dataCenterId must max than 0 and small than maxDataCenterId-[' +
          this.maxDataCenterId +
          ']'
      )
    }

    this.workerId = window.BigInt(_workerId)
    this.dataCenterId = window.BigInt(_dataCenterId)
    this.sequence = window.BigInt(_sequence)
  }
  Snowflake.prototype.tilNextMillis = function (lastTimestamp) {
    var timestamp = this.timeGen()
    while (timestamp <= lastTimestamp) {
      timestamp = this.timeGen()
    }
    return window.BigInt(timestamp)
  }
  Snowflake.prototype.timeGen = function () {
    return window.BigInt(Date.now())
  }
  Snowflake.prototype.nextId = function () {
    var timestamp = this.timeGen()
    if (timestamp < this.lastTimestamp) {
      throw new Error(
        'Clock moved backwards. Refusing to generate id for ' +
          (this.lastTimestamp - timestamp)
      )
    }
    if (this.lastTimestamp === timestamp) {
      this.sequence = (this.sequence + 1n) & this.sequenceMask
      if (this.sequence === 0n) {
        timestamp = this.tilNextMillis(this.lastTimestamp)
      }
    } else {
      this.sequence = 0n
    }
    this.lastTimestamp = timestamp
    return (
      ((timestamp - this.twepoch) << this.timestampLeftShift) |
      (this.dataCenterId << this.dataCenterIdShift) |
      (this.workerId << this.workerIdShift) |
      this.sequence
    )
  }
  return Snowflake
})()
